import { faUserTag, faUsers } from "@fortawesome/free-solid-svg-icons";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { usePermissionModal } from "contexts/PermissionModalContext";
import { formatCurrency, formatCount } from "utils/formatters";
import { DashCard, DashCardHeader, DashCardBody } from "./v2/DashCard";
import DashEmptyState from "./v2/DashEmptyState";

interface VendedorTicketMedio {
  nome: string;
  ticketMedio: number;
  quantidadeVendas: number;
}

interface ObterTicketMedioPorVendedorResponse {
  vendedores: VendedorTicketMedio[];
}

const renderPlaceholderRows = () =>
  Array(5)
    .fill(0)
    .map((_, index) => (
      <tr key={index}>
        <td className="dash-skeleton" style={{ height: 14 }} />
        <td className="dash-skeleton" style={{ height: 14 }} />
        <td className="dash-skeleton" style={{ height: 14 }} />
      </tr>
    ));

const TicketMedioPorVendedor = () => {
  const { getQueryRetryHandler } = usePermissionModal();

  const { data, isLoading, error } = useQuery({
    queryKey: ["ticketMedioPorVendedor"],
    queryFn: async () =>
      (
        await axios.get<ObterTicketMedioPorVendedorResponse>(
          "/api/dashboard/vendas/ticket-medio-vendedores",
        )
      ).data,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: getQueryRetryHandler({
      queryKey: ["ticketMedioPorVendedor"],
      defaultMessage: "Você não possui permissão para visualizar o ticket médio por vendedor.",
      showModal: false,
    }),
  });

  const isAccessDenied = axios.isAxiosError(error) && error?.response?.status === 403;
  if (isAccessDenied) return null;

  const vendedores = data?.vendedores ?? [];

  return (
    <DashCard hoverable={false}>
      <DashCardHeader
        icon={faUserTag}
        variant="accent"
        title="Ticket Médio por Vendedor"
        tooltip="Considera só vendedores com 10+ vendas no período, pra um outlier de baixo volume não aparecer como 'melhor vendedor'."
      />
      <DashCardBody flush>
        {isLoading ? (
          <table className="dash-table">
            <tbody>{renderPlaceholderRows()}</tbody>
          </table>
        ) : vendedores.length === 0 ? (
          <DashEmptyState icon={faUsers} title="Nenhum vendedor encontrado" description="Não há vendas registradas no período." />
        ) : (
          <table className="dash-table">
            <thead>
              <tr>
                <th>Vendedor</th>
                <th className="dash-num">Ticket Médio</th>
                <th className="dash-num">Qtd Vendas</th>
              </tr>
            </thead>
            <tbody>
              {vendedores.map((vendedor, index) => (
                <tr key={index}>
                  <td className="dash-table-primary">{vendedor.nome}</td>
                  <td className="dash-num">{formatCurrency(vendedor.ticketMedio)}</td>
                  <td className="dash-num">{formatCount(vendedor.quantidadeVendas)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DashCardBody>
    </DashCard>
  );
};

export default TicketMedioPorVendedor;
